import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Classes for the relative wrapper div (e.g. spacing like `mt-1`). */
  wrapperClassName?: string;
};

/**
 * Accessible password field with a show/hide eye toggle.
 *
 * - Toggles the underlying input between `password` and `text`.
 * - The toggle is a real <button type="button"> so it's keyboard reachable
 *   (Tab + Enter/Space) and never submits the form; aria-label/aria-pressed
 *   reflect state for screen readers.
 * - Hides the native Edge/IE reveal control (via the `co-password-field`
 *   class) so only this custom toggle is shown.
 *
 * All standard input props (value, onChange, id, name, required, placeholder,
 * autoComplete, etc.) are forwarded to the <input>. Pass the field's visual
 * classes via `className`; `pr-10` is appended automatically to leave room
 * for the icon.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className = '', wrapperClassName = '', ...inputProps }, ref) {
    const [show, setShow] = useState(false);

    return (
      <div className={`relative ${wrapperClassName}`}>
        {/* Suppress the browser's built-in reveal icon so only our toggle shows */}
        <style>{`.co-password-field::-ms-reveal, .co-password-field::-ms-clear { display: none; }`}</style>
        <input
          ref={ref}
          type={show ? 'text' : 'password'}
          className={`co-password-field ${className} pr-10`}
          {...inputProps}
        />
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? 'Hide password' : 'Show password'}
          aria-pressed={show}
          title={show ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
        >
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    );
  },
);
